import { signal } from '@preact/signals';

/**
 * A ~60-line router.
 *
 * A dedicated routing library would add more bytes than this whole module for features a
 * six-view podcast client does not use. The design brief puts bundle size first.
 */

export const currentPath = signal(location.pathname + location.search);

addEventListener('popstate', () => {
  // Back and forward are "backward" travel visually, whichever way the history cursor moved:
  // the page you are returning to should slide in from the side it left towards.
  pendingPath = location.pathname + location.search;
  withViewTransition(
    () => {
      currentPath.value = location.pathname + location.search;
    },
    { direction: 'back' },
  );
});

/**
 * How deep a route sits in the app's information hierarchy.
 *
 * Used only to pick a transition direction: going from a list to a detail page reads as
 * moving forward (new page enters from the trailing edge), and the reverse as coming back.
 * Peers — Home to Search — cross-fade instead, because neither is "inside" the other.
 */
function routeDepth(path: string): number {
  const segments = path.split('?')[0].split('/').filter(Boolean);
  // Home has no segments but is a peer of the other tabs, not their parent.
  return Math.max(1, segments.length);
}

/**
 * The path the app is heading to, which is not the same as the one it is showing.
 *
 * `currentPath` is only assigned inside the view-transition callback, which browsers invoke
 * asynchronously — so two clicks on the same link both saw the old value, both passed the
 * guard, and both pushed a history entry, leaving the reader to press Back twice to escape
 * one link. This records the intent synchronously so the second click is recognised as a
 * repeat of the first.
 */
let pendingPath = currentPath.value;

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === currentPath.value || to === pendingPath) return;
  const from = pendingPath;
  pendingPath = to;
  const delta = routeDepth(to) - routeDepth(from);
  const direction: TransitionDirection = delta > 0 ? 'forward' : delta < 0 ? 'back' : 'none';

  withViewTransition(
    () => {
      history[opts.replace ? 'replaceState' : 'pushState']({}, '', to);
      currentPath.value = to;
      // A fresh view should start at the top, as a real navigation would.
      scrollTo({ top: 0 });
    },
    { direction },
  );
}

/** Intercept in-app anchor clicks so they route client-side. */
export function installLinkInterceptor(): void {
  addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = (event.target as Element | null)?.closest?.('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    if (anchor.getAttribute('rel') === 'external') return;

    // Only same-origin, non-API links are ours to handle.
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/hooks/')) return;

    // A same-page fragment link — a skip link, a footnote reference — is not a route change.
    // Leave it to the browser's native anchor-scroll behaviour rather than routing it, which
    // would either no-op (the "new" path matches the current one) or drop the hash entirely.
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;

    event.preventDefault();
    navigate(url.pathname + url.search);
  });
}

export interface Route {
  name: string;
  params: Record<string, string>;
}

/** Map a pathname onto a view name plus its params. */
export function matchRoute(path: string): Route {
  const pathname = path.split('?')[0];
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return { name: 'home', params: {} };

  switch (segments[0]) {
    case 'search':
      return { name: 'search', params: {} };
    case 'library':
      return { name: 'library', params: {} };
    case 'queue':
      return { name: 'queue', params: {} };
    case 'settings':
      return { name: 'settings', params: {} };
    // Linked from the Play listing, so it must resolve for signed-out visitors too.
    case 'delete-account':
      return { name: 'deleteAccount', params: {} };
    case 'privacy':
      return { name: 'privacy', params: {} };
    case 'podcast':
      return segments[1]
        ? { name: 'podcast', params: { id: segments[1] } }
        : { name: 'notfound', params: {} };
    case 'episode':
      return segments[1]
        ? { name: 'episode', params: { id: segments[1] } }
        : { name: 'notfound', params: {} };
    case 'playlist':
      return segments[1]
        ? { name: 'playlist', params: { id: segments[1] } }
        : { name: 'notfound', params: {} };
    case 'person':
      return segments[1]
        ? { name: 'person', params: { name: decodeURIComponent(segments.slice(1).join('/')) } }
        : { name: 'notfound', params: {} };
    default:
      return { name: 'notfound', params: {} };
  }
}

export type TransitionDirection = 'forward' | 'back' | 'none';

/** Identifies the most recently started transition. See `withViewTransition`. */
let transitionTicket = 0;

/**
 * Run a DOM update inside a view transition when the browser supports one.
 *
 * The direction is published as `data-transition` on `<html>` for the duration of the
 * transition, which is what lets one set of `::view-transition-*` rules in the stylesheet
 * animate three different ways without any JS-driven keyframes.
 *
 * `prefers-reduced-motion` does not skip the transition outright — a hard content swap is
 * itself jarring. It falls through to `data-transition="none"`, whose rules are a plain
 * cross-fade with no movement, which is the accessible form of the same affordance.
 */
export function withViewTransition(
  update: () => void,
  opts: { direction?: TransitionDirection } = {},
): void {
  const start = (
    document as Document & {
      startViewTransition?: (cb: () => void) => { finished?: Promise<unknown> };
    }
  ).startViewTransition;

  if (typeof start !== 'function') {
    update();
    return;
  }

  const reduced =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const direction = reduced ? 'none' : (opts.direction ?? 'none');

  const root = document.documentElement;
  root.dataset.transition = direction;

  /*
   * Only the newest transition owns the flag.
   *
   * Starting a second transition makes the browser skip the first, whose `finished` then
   * settles — and an unconditional cleanup there would strip `data-transition` while the
   * second one is still animating. Every directional rule is gated on that attribute, and
   * the reset above turns the UA cross-fade off, so the survivor would run with no animation
   * at all: a jump cut on exactly the common case of a quick double-tap or tap-then-back.
   */
  const ticket = ++transitionTicket;
  const transition = start.call(document, update);
  const done = transition?.finished;
  const clear = () => {
    if (ticket === transitionTicket) delete root.dataset.transition;
  };
  if (done && typeof done.then === 'function') done.then(clear, clear);
  else clear();
}
